import React, { FC } from 'react';
import { CoaDocument, DocType, DocumentStatus } from '../../../../types';
import DocumentCardLayout from './DocumentCardLayout/DocumentCardLayout';
import { getThumbnail } from './utils/get-thumbnail';
import { trackAction } from '../../../../libs/analytics/ActionTracking';
import { useAppStore } from '../../../../store/appStore';
import EditDocPopup from '../EditDocPopup/EditDocPopup';
import downloadAll from './utils/download-all';
import ViewDocPopup from '../ViewDocPopup';

type CoaDocCardProps = {
  coaDocument: CoaDocument;
  isNeedAttention?: boolean;
};

export const CoaDocCard: FC<CoaDocCardProps> = ({ coaDocument, isNeedAttention }) => {
  const showWindow = useAppStore((state) => state.showWindow);

  const isFixNeeded = coaDocument.document?.status === DocumentStatus.FIX_REQUESTED;
  const attachments = coaDocument.document?.attachments;
  const hasAttachment = Boolean(attachments?.[0]);
  const hasThumbnails = attachments?.some((a) => a.thumbnails?.large);

  function handleUploadButtonClick() {
    trackAction(`Upload new ${DocType.CERTIFICATE_OF_AUTHORIZATION} click`, null);
    showWindow(<EditDocPopup docType={DocType.CERTIFICATE_OF_AUTHORIZATION} projectId={coaDocument.projectId} />);
  }

  function handleEditButtonClick() {
    trackAction(`Edit ${DocType.CERTIFICATE_OF_AUTHORIZATION} click`, null);
    showWindow(
      <EditDocPopup
        docType={DocType.CERTIFICATE_OF_AUTHORIZATION}
        projectId={coaDocument.projectId}
        document={coaDocument.document}
      />,
    );
  }

  function handleShowDocumentAttachments() {
    trackAction(`Show all document attachments click`, null);

    if (attachments) {
      showWindow(<ViewDocPopup docType={DocType.CERTIFICATE_OF_AUTHORIZATION} attachments={attachments} />);
    }
  }

  function handleDownloadAllClick() {
    trackAction('Download all document files button click', null);

    downloadAll(attachments);
  }

  return (
    <DocumentCardLayout
      docType={DocType.CERTIFICATE_OF_AUTHORIZATION}
      projectName={coaDocument.projectName}
      isFixNeeded={isFixNeeded}
      warningDate={isNeedAttention ? coaDocument.document?.endDate : undefined}
      startDate={coaDocument.document?.startDate}
      endDate={coaDocument.document?.endDate}
      thumbnail={getThumbnail(coaDocument.document)}
      isNewDocument={!coaDocument.document}
      button={
        isNeedAttention
          ? isFixNeeded
            ? { title: 'Edit Document', onClick: handleEditButtonClick }
            : { title: 'Upload Document', onClick: handleUploadButtonClick }
          : undefined
      }
      onCardClick={hasThumbnails ? handleShowDocumentAttachments : undefined}
      onDownloadAllClick={hasAttachment ? handleDownloadAllClick : undefined}
    />
  );
};
